import SEARCH_INDEX from "./searchIndex.json";

export const save = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (_) {} };
export const load = (k) => { try { const r = localStorage.getItem(k); return r ? JSON.parse(r) : null; } catch (_) { return null; } };

// The 4 base languages with a cached, clean translated topic name available (see
// scripts/build-search-index.js's `topicNames` output, sourced from /translations/ docs).
// Mirrors that script's own LANGS list — kept as a separate literal here since utils.js is
// browser-bundled and can't require() a Node script.
const TRANSLATED_TOPIC_LANGS = ["Spanish", "German", "French", "Farsi"];

// Returns the topic name to show a learner in their base language. Falls back to the
// English topic.topic_name for any language outside the 4 with cached translations (English,
// Turkish, or an EXTRA_LANGS pick), and also falls back — with a console.warn, same style as
// the frozen-language warning in api.js — if a specific topic_id is missing from the cache
// (e.g. a curriculum batch not yet prewarmed for that language).
export function getTranslatedTopicName(topic, baseLanguage) {
  if (!TRANSLATED_TOPIC_LANGS.includes(baseLanguage)) return topic.topic_name;
  const name = SEARCH_INDEX.topicNames?.[topic.topic_id]?.[baseLanguage];
  if (!name) {
    console.warn(`Topic name translation missing for '${topic.topic_id}' in '${baseLanguage}' — falling back to English`);
    return topic.topic_name;
  }
  return name;
}

export function fmtWpd(n) { return n >= 10 ? Math.round(n) : n.toFixed(1); }

const FA_DIGITS = "۰۱۲۳۴۵۶۷۸۹";

// Renders a number in the script the active language expects: Western digits
// everywhere, Eastern Arabic-Indic digits for Farsi. When `n` is a genuine
// number, Farsi output goes through Intl.NumberFormat so grouping/decimal
// separators follow Persian convention (٬ and ٫) instead of Western ones.
// When `n` is already a pre-formatted string (e.g. a "99+" badge cap), only
// the ASCII digit characters are swapped and everything else is left as-is.
export function formatNumber(n, lang, { grouping = false } = {}) {
  if (lang !== "Farsi") {
    return typeof n === "number" ? (grouping ? n.toLocaleString() : String(n)) : String(n);
  }
  const str = typeof n === "number"
    ? new Intl.NumberFormat("fa-IR", { useGrouping: grouping }).format(n)
    : String(n);
  return str.replace(/[0-9]/g, d => FA_DIGITS[d]);
}

// Whole-dollar-plus-cents money display (e.g. a commission balance), USD only — same "$" +
// formatNumber() convention already used for subscription pricing in App.js's PaywallScreen
// (formatNumber handles the Farsi Eastern Arabic-Indic digit conversion; the "$" itself is
// assumed universal, same as there). Matches that call site's own precision behavior — a whole
// number renders with no decimal places, same as $9/mo does today — rather than forcing a
// stricter always-2-decimals format that would look inconsistent next to existing price displays.
export function fmtMoneyCents(cents, lang) {
  return `$${formatNumber(Math.round(cents || 0) / 100, lang)}`;
}

// Voice-minutes formatting, shared by SettingsView (usage row) and the Listen & Speak Lab
// (topic-list balance card) so the two never drift. Two variants on purpose:
//   formatVoiceMinutes — whole minutes ("3h 0m" / "45m"). For limits and purchased packs,
//     which are always granted in whole-minute amounts.
//   formatVoiceDuration — down to the second ("2h 49m 30s" / "36s"). For any metered figure
//     (used / remaining): voice usage accumulates as raw floats (Google STT billedDuration +
//     each TTS clip's audio.duration), so whole-minute rounding hid almost all of a light
//     user's real consumption — 4m 50s and 5m 30s both read as "5m".
export function formatVoiceMinutes(seconds) {
  const mins = Math.round(Math.max(0, seconds) / 60);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export function formatVoiceDuration(seconds) {
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const parts = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  if (s > 0 || parts.length === 0) parts.push(`${s}s`);
  return parts.join(" ");
}

export function daysSince(val) {
  if (!val) return 0;
  let ms;
  if (typeof val === "string") ms = new Date(val).getTime();
  else if (typeof val.toMillis === "function") ms = val.toMillis(); // live Firestore Timestamp
  else if (typeof val.seconds === "number") ms = val.seconds * 1000; // serialized {seconds, nanoseconds}
  else return 0;
  return Math.max(0, Math.floor((Date.now() - ms) / 86400000));
}

export function todayISO() { return new Date().toISOString().slice(0, 10); }
export function addDays(iso, n) { const d = new Date(iso); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); }
export function daysBetween(iso1, iso2) { return Math.max(0, Math.round((new Date(iso2) - new Date(iso1)) / 86400000)); }

export function defaultProgress() {
  return {
    wordsLearned: 0, lessonsCompleted: 0, streak: 0, protectionDays: 0, lastStudy: null, dailyLog: [], totalMinutes: 0,
    // longestStreak/longestStreakAt: running all-time-best streak + the date it was last set,
    // used by the Babi Weekly Recap to tell whether a "longest streak yet" record was broken
    // within the specific week being recapped (see babiCheckins.js).
    longestStreak: 0, longestStreakAt: null,
    // Per-day dedup for the Babi Weekly Recap / Daily Study Plan pop-ups (babiCheckins.js) —
    // lastBabiPopupDate gates "at most one per day", lastRecapWeekStart records which week's
    // recap has already been delivered so a late-week first-open doesn't re-fire it.
    lastBabiPopupDate: null, lastBabiPopupType: null, lastRecapWeekStart: null,
    // Listen & Speak Lab progress (plan doc §7) — nested inside `progress`, not top-level,
    // deliberately: this whole object is what handleSwitchTargetLanguage archives/restores
    // wholesale on a target-language switch (see applyLabSessionCompletion below), so these
    // ride that existing swap for free instead of needing their own language-keying, unlike
    // the separate top-level `grammarProgress` field (which does NOT get swapped).
    labSessionsCompleted: 0, labMinutes: 0, labGrammarTally: {},
  };
}

// Listen & Speak Lab session completion (plan doc §7) — the pure state-transition half of
// App.js's handleLabSessionComplete, extracted here (not left inline in App.js) so it's
// actually unit-testable: App.js transitively imports useVoiceTurn -> @ricky0123/vad-web,
// which this project's Jest environment can't resolve (the same pre-existing issue noted in
// ListenSpeakLab.recap.test.js), so nothing inside App.js itself can be imported by a test.
// `today` defaults to todayISO() but takes an override so tests don't depend on wall-clock date.
// Returns { updProg, streakResult } — the caller (App.js) still owns the side effects
// (setProgress/save/saveUserData/the streak-alert banner), unchanged from before this split.
export function applyLabSessionCompletion(progress, { learnerTurns = 0, minutes = 0, grammarTally = {} } = {}, today = todayISO()) {
  const updProg = { ...progress };
  updProg.lastStudy = today;
  updProg.totalMinutes = (updProg.totalMinutes || 0) + minutes;
  updProg.labSessionsCompleted = (updProg.labSessionsCompleted || 0) + 1;
  updProg.labMinutes = (updProg.labMinutes || 0) + minutes;
  updProg.labGrammarTally = { ...(updProg.labGrammarTally || {}) };
  for (const [gpId, count] of Object.entries(grammarTally || {})) {
    updProg.labGrammarTally[gpId] = (updProg.labGrammarTally[gpId] || 0) + count;
  }
  // Pre-existing bug fixed while extracting this (not part of the §7 ask, flagged per project
  // convention): `{ ...progress }` above is a shallow copy, so updProg.dailyLog was literally
  // `progress.dailyLog` (same array reference) — the original inline App.js code then mutated
  // it directly (`.push`, or writing straight onto the found entry), silently mutating the OLD
  // progress state object before setProgress(updProg) ever ran. Caught by this function's own
  // "does not mutate the input" test. Fixed here by cloning the array (and the matched entry)
  // before writing, same immutable-update discipline the rest of this codebase already expects.
  updProg.dailyLog = [...progress.dailyLog];
  const logIdx = updProg.dailyLog.findIndex(l => l.date === today);
  if (logIdx !== -1) {
    const log = updProg.dailyLog[logIdx];
    updProg.dailyLog[logIdx] = { ...log, minutes: (log.minutes || 0) + minutes, labTurns: (log.labTurns || 0) + learnerTurns };
  } else {
    updProg.dailyLog.push({ date: today, words: 0, lessons: 0, minutes, labTurns: learnerTurns });
  }
  const streakResult = computeStreakUpdate(progress.streak, progress.protectionDays, progress.lastStudy, today);
  updProg.streak = streakResult.streak;
  updProg.protectionDays = streakResult.newProtectionDays;
  if (updProg.streak > (progress.longestStreak || 0)) {
    updProg.longestStreak = updProg.streak;
    updProg.longestStreakAt = today;
  }
  return { updProg, streakResult };
}

// Legacy formula, kept only to backfill `progress.protectionDays` for accounts saved
// before that field existed (see computeStreakUpdate's `oldBank` fallback below).
export function getProtectionDays(streak) {
  return Math.floor((streak || 0) / 3);
}

// Every 3rd consecutive active day banks 1 "streak protection day" — a real spendable
// balance persisted in `progress.protectionDays`, not re-derived from the streak count
// each time (that previously let the same days be "spent" over and over: decaying the
// streak by N after a gap doesn't reliably move floor(streak/3) down by N, so the badge
// could stay put after a miss instead of dropping). A gap that fits within the current
// balance decays the streak by exactly the number of missed days and spends that many
// protection days; a gap that exceeds the balance wipes the streak back to today (day 1)
// and the balance to 0, same as any ordinary streak reset.
// Returns `newProtectionDays` (the balance to persist as progress.protectionDays going
// forward) always, plus `protectionDays` (the pre-gap balance, for the "you had X banked"
// messaging) only when a gap actually occurred.
export function computeStreakUpdate(oldStreak, oldBank, lastStudy, today) {
  const bank = oldBank ?? getProtectionDays(oldStreak);
  if (!lastStudy) return { streak: 1, newProtectionDays: 0, missedDays: 0, wiped: false };
  const gap = daysBetween(lastStudy, today);
  if (gap === 0) return { streak: oldStreak || 1, newProtectionDays: bank, missedDays: 0, wiped: false };
  if (gap === 1) {
    const streak = (oldStreak || 1) + 1;
    return { streak, newProtectionDays: bank + (streak % 3 === 0 ? 1 : 0), missedDays: 0, wiped: false };
  }
  const missedDays = gap - 1;
  if (missedDays > bank) return { streak: 1, newProtectionDays: 0, missedDays, protectionDays: bank, wiped: true };
  return { streak: oldStreak - missedDays, newProtectionDays: bank - missedDays, missedDays, protectionDays: bank, wiped: false };
}

export function cardDist(cards) {
  return (cards || []).reduce((a, c) => {
    const iv = c.interval || 0;
    if (!c.stability || iv < 3) a[0]++;
    else if (iv < 21) a[1]++;
    else if (iv < 90) a[2]++;
    else a[3]++;
    return a;
  }, [0, 0, 0, 0]);
}

// Wraps an interpolated value in <bdi> so its own text direction resolves
// independently of the surrounding translated sentence. Without this, a
// Turkish/English value embedded in an RTL (Farsi/Arabic) UI string lets the
// paragraph's bidi resolution pull the value's trailing punctuation (e.g. a
// "?") to the wrong visual side.
export function isolateInterpolation(template, placeholder, value) {
  const token = `{${placeholder}}`;
  const idx = template.indexOf(token);
  if (idx === -1) return template;
  return (
    <>
      {template.slice(0, idx)}
      <bdi>{value}</bdi>
      {template.slice(idx + token.length)}
    </>
  );
}

// Strips markdown / emphasis syntax that text-to-speech would otherwise read out literally
// ("asterisk, yes, asterisk", "hash hash Heading"). A safety net for spoken replies where a
// "no markdown" prompt instruction can't be fully trusted — used by Listen & Speak Lab for
// both the on-screen bubble and the string handed to textToSpeech(), so the two match.
export function stripSpokenMarkdown(text) {
  if (typeof text !== "string") return text;
  return text
    .replace(/\*\*([^*]+)\*\*/g, "$1")               // **bold**
    .replace(/\*([^*\n]+)\*/g, "$1")                 // *italic*
    .replace(/__([^_]+)__/g, "$1")                   // __bold__
    .replace(/`([^`]+)`/g, "$1")                     // `code`
    .replace(/^[ \t]*#{1,6}[ \t]+/gm, "")            // # headings
    .replace(/^[ \t]*([*+-]|\d+[.)])[ \t]+/gm, "")   // -, *, 1. list markers
    .replace(/^[ \t]*>[ \t]?/gm, "")                 // > blockquote
    .replace(/[*_`~]/g, "")                          // any stray emphasis marks left over
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}
